import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, MapPin, Briefcase, GraduationCap, Home, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { SessionCard } from '../components/SessionCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const countryInfo = {
  DE: {
    name: 'Germany',
    flag: '🇩🇪',
    image: 'https://images.unsplash.com/photo-1467269204594-9661b134dd2b?w=1200',
    capital: 'Berlin',
    language: 'German',
    currency: 'Euro (€)',
    highlights: [
      'Free public university education',
      'Strong job market for tech & engineering',
      'Opportunity Card (Chancenkarte) for job seekers',
      'Ausbildung vocational training programs',
      'Path to permanent residency in 21-33 months'
    ],
    visaTypes: ['Student Visa', 'Job Seeker Visa', 'Work Visa', 'Opportunity Card', 'Ausbildung Visa']
  },
  NL: {
    name: 'Netherlands',
    flag: '🇳🇱',
    image: 'https://images.unsplash.com/photo-1534351590666-13e3e96b5017?w=1200',
    capital: 'Amsterdam',
    language: 'Dutch (English widely spoken)',
    currency: 'Euro (€)',
    highlights: [
      'Highest English proficiency in non-native Europe',
      '30% tax ruling for skilled migrants',
      'Strong tech and startup ecosystem',
      'High quality of life',
      'Excellent work-life balance'
    ],
    visaTypes: ['Study Visa', 'Highly Skilled Migrant Visa', 'Orientation Year Visa', 'Startup Visa']
  },
  IT: {
    name: 'Italy',
    flag: '🇮🇹',
    image: 'https://images.unsplash.com/photo-1515542622106-78bda8ba0e5b?w=1200',
    capital: 'Rome',
    language: 'Italian',
    currency: 'Euro (€)',
    highlights: [
      'Rich cultural heritage',
      'Affordable cost of living',
      'Growing tech sector in Milan',
      'Elective residence visa options',
      'Path to EU citizenship'
    ],
    visaTypes: ['Student Visa', 'Work Visa', 'Elective Residence Visa', 'Digital Nomad Visa']
  },
  FR: {
    name: 'France',
    flag: '🇫🇷',
    image: 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=1200',
    capital: 'Paris',
    language: 'French',
    currency: 'Euro (€)',
    highlights: [
      'World-class education system',
      'Talent Passport for skilled workers',
      'Vibrant startup scene',
      'High quality healthcare',
      'Rich cultural experiences'
    ],
    visaTypes: ['Student Visa', 'Talent Passport', 'Work Visa', 'Startup Visa']
  },
  PL: {
    name: 'Poland',
    flag: '🇵🇱',
    image: 'https://images.unsplash.com/photo-1519197924294-4ba991a11128?w=1200',
    capital: 'Warsaw',
    language: 'Polish',
    currency: 'Polish Złoty (PLN)',
    highlights: [
      'Fastest growing economy in EU',
      'High demand for IT professionals',
      'Very affordable cost of living',
      'Large Indian expat community',
      'Easy work permit process'
    ],
    visaTypes: ['Student Visa', 'Work Visa', 'Business Visa', 'EU Blue Card']
  },
  ES: {
    name: 'Spain',
    flag: '🇪🇸',
    image: 'https://images.unsplash.com/photo-1539037116277-4db20889f2d4?w=1200',
    capital: 'Madrid',
    language: 'Spanish',
    currency: 'Euro (€)',
    highlights: [
      'Digital Nomad Visa available',
      'Mediterranean lifestyle',
      'Growing tech hubs in Barcelona & Madrid',
      'Affordable living outside major cities',
      'Path to permanent residency'
    ],
    visaTypes: ['Student Visa', 'Work Visa', 'Digital Nomad Visa', 'Non-Lucrative Visa']
  },
  PT: {
    name: 'Portugal',
    flag: '🇵🇹',
    image: 'https://images.unsplash.com/photo-1555881400-74d7acaacd8b?w=1200',
    capital: 'Lisbon',
    language: 'Portuguese',
    currency: 'Euro (€)',
    highlights: [
      'One of the easiest EU entry points',
      'D7 Passive Income Visa',
      'NHR tax benefits for 10 years',
      'Thriving startup ecosystem',
      'Excellent weather year-round'
    ],
    visaTypes: ['D7 Visa', 'Tech Visa', 'Student Visa', 'Digital Nomad Visa', 'Startup Visa']
  },
  BE: {
    name: 'Belgium',
    flag: '🇧🇪',
    image: 'https://images.unsplash.com/photo-1491557345352-5929e343eb89?w=1200',
    capital: 'Brussels',
    language: 'Dutch, French, German',
    currency: 'Euro (€)',
    highlights: [
      'Heart of the European Union',
      'Multinational headquarters hub',
      'High salaries in EU institutions',
      'Central location for travel',
      'Diverse expat community'
    ],
    visaTypes: ['Student Visa', 'Work Permit', 'EU Blue Card', 'Single Permit']
  },
  SE: {
    name: 'Sweden',
    flag: '🇸🇪',
    image: 'https://images.unsplash.com/photo-1509356843151-3e7d96241e11?w=1200',
    capital: 'Stockholm',
    language: 'Swedish (English widely spoken)',
    currency: 'Swedish Krona (SEK)',
    highlights: [
      'Innovation and tech leader',
      'Excellent social benefits',
      'High English proficiency',
      'Work-life balance culture',
      'Path to permanent residency'
    ],
    visaTypes: ['Student Visa', 'Work Permit', 'Job Seeker Visa', 'Startup Visa']
  },
  FI: {
    name: 'Finland',
    flag: '🇫🇮',
    image: 'https://images.unsplash.com/photo-1538332576228-eb5b4c4de6f5?w=1200',
    capital: 'Helsinki',
    language: 'Finnish, Swedish',
    currency: 'Euro (€)',
    highlights: [
      'World\'s happiest country',
      'Free university education',
      'Strong tech and gaming industry',
      'Excellent quality of life',
      'Startup-friendly environment'
    ],
    visaTypes: ['Student Visa', 'Work Visa', 'Startup Permit', 'Specialist Visa']
  }
};

export const CountryDetailPage = () => {
  const { code } = useParams();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  const country = countryInfo[code?.toUpperCase()] || null;

  useEffect(() => {
    const fetchSessions = async () => {
      try {
        const response = await axios.get(`${API}/sessions?country_code=${code}`);
        setSessions(response.data);
      } catch (error) {
        console.error('Error fetching sessions:', error);
      } finally {
        setLoading(false);
      }
    };
    if (code) {
      fetchSessions();
    }
  }, [code]);

  if (!country) {
    return (
      <div className="min-h-screen pt-24 pb-16 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-[#002A76] mb-4">Country not found</h1>
          <Link to="/#countries">
            <Button className="bg-[#002A76] hover:bg-[#001F5C] text-white rounded-full">
              <ArrowLeft className="mr-2" size={16} />
              Back to Countries
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="country-detail-page" className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative h-[50vh] md:h-[60vh]">
        <img 
          src={country.image} 
          alt={country.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#002A76]/90 via-[#002A76]/40 to-transparent" />
        
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 w-full">
            <Link 
              to="/#countries"
              className="inline-flex items-center text-white/80 hover:text-white mb-4 transition-colors"
            >
              <ArrowLeft size={16} className="mr-2" />
              All Countries
            </Link>
            <div className="flex items-center gap-4 mb-4">
              <span className="text-6xl md:text-7xl">{country.flag}</span>
              <div>
                <h1 className="text-4xl md:text-5xl font-bold text-white">{country.name}</h1>
                <p className="text-white/80 mt-1">{country.capital} • {country.language}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-wrap justify-center gap-8 md:gap-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#0AA06A]/10 flex items-center justify-center">
                <GraduationCap size={20} className="text-[#0AA06A]" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Study</p>
                <p className="font-semibold text-[#002A76]">Universities</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#002A76]/10 flex items-center justify-center">
                <Briefcase size={20} className="text-[#002A76]" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Work</p>
                <p className="font-semibold text-[#002A76]">Jobs Available</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#F59E0B]/10 flex items-center justify-center">
                <Home size={20} className="text-[#F59E0B]" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Settle</p>
                <p className="font-semibold text-[#002A76]">PR Pathway</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs defaultValue="sessions" className="space-y-8">
            <TabsList className="bg-[#F7F4F0] p-1 rounded-full w-full max-w-md mx-auto grid grid-cols-2">
              <TabsTrigger 
                value="sessions"
                data-testid="tab-sessions"
                className="rounded-full data-[state=active]:bg-[#002A76] data-[state=active]:text-white"
              >
                Live Sessions
              </TabsTrigger>
              <TabsTrigger 
                value="overview"
                data-testid="tab-overview"
                className="rounded-full data-[state=active]:bg-[#002A76] data-[state=active]:text-white"
              >
                Overview
              </TabsTrigger>
            </TabsList>

            {/* Sessions Tab */}
            <TabsContent value="sessions" className="mt-8">
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-[#002A76] mb-2">
                  Upcoming Sessions for {country.name}
                </h2>
                <p className="text-gray-600">
                  Join our live sessions and masterclasses focused on {country.name}
                </p>
              </div>

              {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="bg-white rounded-2xl overflow-hidden animate-pulse">
                      <div className="aspect-video bg-gray-200" />
                      <div className="p-5">
                        <div className="h-6 bg-gray-200 rounded w-3/4 mb-3" />
                        <div className="h-4 bg-gray-200 rounded w-full mb-2" />
                        <div className="h-4 bg-gray-200 rounded w-2/3" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : sessions.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {sessions.map((session) => (
                    <SessionCard key={session.id} session={session} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-16 bg-[#F7F4F0] rounded-2xl">
                  <p className="text-gray-600 mb-4">No upcoming sessions for {country.name} yet.</p>
                  <Link to="/sessions">
                    <Button className="bg-[#002A76] hover:bg-[#001F5C] text-white rounded-full">
                      View All Sessions
                    </Button>
                  </Link>
                </div>
              )}
            </TabsContent>

            {/* Overview Tab */}
            <TabsContent value="overview" className="mt-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Highlights */}
                <div className="bg-white rounded-2xl p-6 md:p-8 border border-gray-100">
                  <h3 className="text-xl font-bold text-[#002A76] mb-6">Why {country.name}?</h3>
                  <ul className="space-y-4">
                    {country.highlights.map((highlight, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full bg-[#0AA06A] flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-white text-sm font-bold">{index + 1}</span>
                        </div>
                        <span className="text-gray-700">{highlight}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Visa Types */}
                <div className="bg-white rounded-2xl p-6 md:p-8 border border-gray-100">
                  <h3 className="text-xl font-bold text-[#002A76] mb-6">Visa Options</h3>
                  <div className="flex flex-wrap gap-3">
                    {country.visaTypes.map((visa, index) => (
                      <span 
                        key={index}
                        className="px-4 py-2 bg-[#F7F4F0] text-[#002A76] rounded-full text-sm font-medium"
                      >
                        {visa}
                      </span>
                    ))}
                  </div>
                  <div className="mt-8 p-4 bg-[#0AA06A]/10 rounded-xl">
                    <p className="text-sm text-[#002A76]">
                      <strong>Not sure which visa is right for you?</strong> Get a free profile evaluation to find the best pathway.
                    </p>
                    <Link to="/profile-evaluation">
                      <Button className="mt-4 bg-[#0AA06A] hover:bg-[#088A5B] text-white rounded-full">
                        Get Free Evaluation
                        <ArrowRight className="ml-2" size={16} />
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-16 bg-[#002A76]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Ready to Explore {country.name}?
          </h2>
          <p className="text-gray-300 mb-8">
            Book a 1:1 call with our expat advisors to discuss your specific situation
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/book-call">
              <Button className="bg-[#0AA06A] hover:bg-[#088A5B] text-white rounded-full px-8 py-3 font-semibold">
                Book 1:1 Call
              </Button>
            </Link>
            <Link to="/profile-evaluation">
              <Button 
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-[#002A76] rounded-full px-8 py-3 font-semibold"
              >
                Profile Evaluation
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CountryDetailPage;
